import type { Route } from 'next';
import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';
import type { CollectionPage, ListItem, WithContext } from 'schema-dts';

import { ARTICLE_LIST_ITEM_SIZES, ArticleListItem } from '#components/Article/Items/ArticleListItem';
import { ImageModule } from '#components/Image/ImageModule';
import { SchemaOrg } from '#components/SchemaOrg';
import { useCurrentDate } from '#cutils/context/TimeContext';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import { TrackEventDataType } from '#types/tracking';
import { articleIsBreakingNews } from '#utils/article/articleIsBreakingNews';
import { PageView } from 'react-tracktor';

import styles from './TagPage.module.scss';
import type { TagPageQuery } from './__generated__/TagPage.generated';
import { RichText } from './base/RichtText/RichtText';

type Props = {
	tagPage: NonNullable<NonNullable<TagPageQuery['tagPageById']>['nodes'][number]>;
};

export function SubjectContainer({ tagPage }: Props) {
	const currentDate = useCurrentDate();

	const articles = (tagPage.boardsSectionsByBoardId.nodes[0]?.boardsSectionsItemsByBoardsSectionId.nodes ?? [])
		.filter(notNullOrUndefined)
		.map(({ articleByArticleId }) => articleByArticleId)
		.filter(notNullOrUndefined);

	const canonicalUrl = tagPage.shareUrl;
	const seoTitle = `${tagPage.seoTitle || tagPage.tagByTagId?.text} - aktuelle Nachrichten von BR24`;

	const subjectArticlesSchema: WithContext<CollectionPage> = {
		'@context': 'https://schema.org',
		'@type': 'CollectionPage',
		name: seoTitle,
		description: `Bleiben Sie informiert: aktuelle Nachrichten, Fakten und Hintergründe zum Thema ${tagPage.tagByTagId?.text} finden Sie auf BR24.`,
		'@id': canonicalUrl,
		mainEntity: {
			'@type': 'ItemList',
			itemListElement: articles.map((article, index): WithContext<ListItem> => ({
				'@context': 'https://schema.org',
				'@type': 'ListItem',
				name: article.title,
				url: article.shareUrl,
				position: index + 1,
			})),
			numberOfItems: articles.length,
		},
		copyrightYear: currentDate.getFullYear(),
	};

	return (
		<>
			<Head>
				{generateNextSeo({
					canonical: canonicalUrl,
					title: seoTitle,
					description: `Bleiben Sie informiert: aktuelle Nachrichten, Fakten und Hintergründe zum Thema ${tagPage.tagByTagId?.text} finden Sie auf BR24.`,
				})}
			</Head>

			<SchemaOrg schema={subjectArticlesSchema} />

			<PageView
				key={canonicalUrl}
				pageViewData={{
					data: tagPage.trackingInfos ?? {},
					event: { page: canonicalUrl, type: 'PageViewEvent' },
				}}
			/>

			<div className={styles.wrapper}>
				<h1 className={`${styles.title} heading3`}>Neuste Nachrichten zum Thema: {tagPage.tagByTagId?.text}</h1>

				{tagPage.hasIntroductionText ? (
					<div className={styles.richTextDiv}>
						<div className={styles.richInnerDiv}>
							<h2 className={styles.richHeadline}>{tagPage.introductionHeadline}</h2>
							<RichText className="body4" text={tagPage.introductionText} />
						</div>
					</div>
				) : null}

				{articles.map((article) => {
					const isBreakingNews = articleIsBreakingNews(article, currentDate);
					const modulesTypes = article.modulesTypes.filter(notNullOrUndefined);
					const image = <ImageModule image={article.image} sizes={ARTICLE_LIST_ITEM_SIZES} />;

					return (
						<ArticleListItem
							key={article.rowId}
							as="h3"
							image={image}
							isBreakingNews={isBreakingNews}
							modulesTypes={modulesTypes}
							internalPath={article.path as Route}
							publicationDate={article.publicationDate}
							teaserText={article.teaserText}
							title={article.title}
							trackEventDataType={TrackEventDataType.TopArticle}
						/>
					);
				})}
			</div>
		</>
	);
}
